import {
  useTranslate,
  IResourceComponentsProps,
  useExport,
  HttpError,
  CrudFilters,
  useSelect,
  getDefaultFilter,
  useImport,
} from "@refinedev/core";

import { List, useTable } from "@refinedev/antd";
import {
  Table,
  Button,
  Row,
  Col,
  Card,
  FormProps,
  Form,
  Select,
  Input,
  InputRef,
} from "antd";

import {
  IApiKey,
  IApiKeyFilterVariables,
} from "interfaces";
import { useRef } from "react";
import { ApiKeyActions } from "components/apiKeyActions";

export const ApiKeyList: React.FC<IResourceComponentsProps> = () => {
  const {
    tableProps,
    filters,
    searchFormProps,
  } = useTable<IApiKey, HttpError, IApiKeyFilterVariables>({
    resource: "keys",

    onSearch: (params) => {
      const filters: any = [];

      const { service } = params;

      filters.push({
        field: "service",
        operator: "eq",
        value: service,
      });

      return filters;
    },
  });

  const t = useTranslate();

  const { triggerExport, isLoading: isExportLoading } = useExport<IApiKey>({
    resource: "keys",
    mapData: (item) => item,
  });

  const { inputProps, isLoading: isImportLoading } = useImport<IApiKey>({
    resource: "keys",
    batchSize: 1,
  });

  const importInputRef = useRef<InputRef>(null!)

  return (
    <Row gutter={[16, 16]}>
      <Col
        xl={6}
        lg={24}
        xs={24}
        style={{
          marginTop: "52px",
        }}
      >
        <Card title={t("orders.filter.title")}>
          <Filter formProps={searchFormProps} filters={filters || []} />
        </Card>
      </Col>{" "}
      <Col xl={18} xs={24}>
        <List>
          <div style={{ display: "flex", gap: 4 }}>
            <Button
              type="primary"
              disabled={isImportLoading}
              onClick={() => importInputRef.current.input?.click()}
            >
              {isImportLoading ? "Importing..." : "Import from CSV"}
            </Button>

            <Button
              type="primary"
              disabled={isExportLoading}
              onClick={triggerExport}
            >
              {isExportLoading ? "Exporting..." : "Export as CSV"}
            </Button>

            <Input
              name="csv"
              hidden
              ref={importInputRef}
              {...inputProps}
            />
          </div>

          <Table {...tableProps} rowKey="id">
            <Table.Column<IApiKey>
              key="service"
              dataIndex={"service"}
              title={t("Service")}
              render={(value) => value}
            />
            <Table.Column<IApiKey>
              key="description"
              dataIndex={"description"}
              title={t("Description")}
              render={(value) => value}
            />
            <Table.Column<IApiKey>
              key="apiKey"
              dataIndex={"apiKey"}
              title={t("API Key")}
              render={(value) => value}
            />
            <Table.Column<IApiKey>
              fixed="right"
              title={t("table.actions")}
              dataIndex="actions"
              key="actions"
              align="center"
              render={(_value, record) => <ApiKeyActions record={record} />}
            />
          </Table>
        </List>
      </Col>
    </Row>
  );
};

const Filter: React.FC<{ formProps: FormProps; filters: CrudFilters }> = (
  props
) => {
  const t = useTranslate();
  const { formProps, filters } = props;
  const apiKeyServiceProps = useSelect<IApiKey>({
    resource: "keys",
    optionLabel: "service" as any,
    optionValue: "service" as any,
    defaultValue: getDefaultFilter("apikey.service", filters),

    pagination: {
      mode: "server",
    },
  });

  return (
    <Form
      layout="vertical"
      {...formProps}
      initialValues={{
        status: getDefaultFilter("apikey.service", filters),
      }}
    >
      <Row gutter={[10, 0]} align="bottom">
        <Col xl={24} md={8} sm={12} xs={24}>
          <Form.Item label={"Service"} name="service">
            <Select
              options={apiKeyServiceProps.options}
              allowClear
              placeholder={"Search services"}
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Col>
        <Col xl={24} md={8} sm={12} xs={24}>
          <Form.Item>
            <Button htmlType="submit" type="primary" size="large" block>
              {t("orders.filter.submit")}
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
